import { useQuery } from '@tanstack/react-query'
import { useMemo } from 'react'

import type { Organization } from 'src/api/main'
import { applicationsApplicationsListOptions } from 'src/api/main'
import { useWorkspaceUsersList } from 'src/features/users/hooks'

import { MAX_APP_NODES, MAX_USER_NODES, NODE_RADIUS } from './constants'
import { MOCK_EDGES, MOCK_NODES } from './mockData'
import type { GraphEdge, GraphModel, GraphNode } from './types'

type Args = { workspaceId: number; organization: Organization | undefined }

type Result = GraphModel & { isLoading: boolean }

const realNode = (
  id: string,
  label: string,
  kind: GraphNode['kind'],
  facts: GraphNode['facts']
): GraphNode => ({
  id,
  label,
  kind,
  radius: NODE_RADIUS[kind],
  facts,
  sample: false
})

const edge = (target: string, flow?: boolean): GraphEdge => ({
  id: `company->${target}`,
  source: 'company',
  target,
  flow
})

/**
 * Hybrid graph model: real Company + Apps + Users from existing endpoints,
 * mock Databases / Email / AI Agents (flagged `sample`) until those backends
 * exist. Overflow beyond the caps collapses into aggregate "+N" nodes.
 */
export const useGraphData = ({ workspaceId, organization }: Args): Result => {
  const idValid = Number.isFinite(workspaceId) && workspaceId > 0

  const appsQuery = useQuery({
    ...applicationsApplicationsListOptions({
      query: { organization: workspaceId, per_page: 100 }
    }),
    enabled: idValid
  })

  const usersQuery = useWorkspaceUsersList({
    workspaceId,
    enabled: idValid,
    query: { per_page: 100 }
  })

  const apps = appsQuery.data?.results
  const { users } = usersQuery

  return useMemo<Result>(() => {
    const company: GraphNode = {
      id: 'company',
      label: organization?.name ?? 'Company',
      kind: 'company',
      radius: NODE_RADIUS.company,
      sample: false,
      facts: [
        { label: 'Domain', value: organization?.domain_name || '—' },
        { label: 'Created', value: organization?.date_created ?? '—' }
      ]
    }

    const appNodes: GraphNode[] = []
    const appEdges: GraphEdge[] = []
    const appList = apps ?? []
    appList.slice(0, MAX_APP_NODES).forEach((a) => {
      const id = `app-${a.id}`
      appNodes.push(
        realNode(id, a.name, 'app', [
          { label: 'Domain', value: a.domain || '—' },
          { label: 'Created', value: a.date_created }
        ])
      )
      appEdges.push(edge(id, true))
    })
    if (appList.length > MAX_APP_NODES) {
      const id = 'app-more'
      appNodes.push(
        realNode(id, `+${appList.length - MAX_APP_NODES} apps`, 'app', [
          { label: 'Total apps', value: String(appList.length) }
        ])
      )
      appEdges.push(edge(id))
    }

    const userNodes: GraphNode[] = []
    const userEdges: GraphEdge[] = []
    users.slice(0, MAX_USER_NODES).forEach((u) => {
      const id = `user-${u.id}`
      const name =
        `${u.user.first_name} ${u.user.last_name}`.trim() || u.user.email
      userNodes.push(
        realNode(id, name, 'user', [
          { label: 'Role', value: u.role },
          { label: 'Status', value: u.status }
        ])
      )
      userEdges.push(edge(id))
    })
    if (users.length > MAX_USER_NODES) {
      const id = 'user-more'
      userNodes.push(
        realNode(id, `+${users.length - MAX_USER_NODES} users`, 'user', [
          { label: 'Members', value: String(users.length) }
        ])
      )
      userEdges.push(edge(id))
    }

    const nodes: GraphNode[] = [
      company,
      ...appNodes,
      ...userNodes,
      ...MOCK_NODES.map((n) => ({ ...n }))
    ]
    // Inter-node interaction edges (mock) so data visibly flows between systems:
    // an AI agent calls an app, an app queries a database, etc.
    const crossEdges: GraphEdge[] = []
    const firstApp = appNodes.find((n) => n.id !== 'app-more')
    const secondApp = appNodes.filter((n) => n.id !== 'app-more')[1]
    if (firstApp) {
      crossEdges.push({
        id: `agent-support->${firstApp.id}`,
        source: 'agent-support',
        target: firstApp.id,
        flow: true
      })
      crossEdges.push({
        id: `${firstApp.id}->db-pg`,
        source: firstApp.id,
        target: 'db-pg',
        flow: true
      })
      crossEdges.push({
        id: `${firstApp.id}->email-smtp`,
        source: firstApp.id,
        target: 'email-smtp',
        flow: true
      })
    }
    if (secondApp) {
      crossEdges.push({
        id: `agent-sync->${secondApp.id}`,
        source: 'agent-sync',
        target: secondApp.id,
        flow: true
      })
    }
    crossEdges.push({
      id: 'agent-sync->db-redis',
      source: 'agent-sync',
      target: 'db-redis',
      flow: true
    })

    const edges: GraphEdge[] = [
      ...appEdges,
      ...userEdges,
      ...MOCK_EDGES.map((e) => ({ ...e })),
      ...crossEdges
    ]

    return {
      nodes,
      edges,
      isLoading: appsQuery.isLoading || usersQuery.isLoading
    }
  }, [organization, apps, users, appsQuery.isLoading, usersQuery.isLoading])
}
